import store from '@/store/index'
import i18n from '@/i18n'

/**
 * Servicio para interactuar con la API de Microsoft Graph.
 */
class GraphService {
  /**
   * Crea un evento de calendario en el calendario de Outlook del usuario.
   * @param {Object} alert - El objeto de la alerta { date, text }.
   * @param {Object} asset - El objeto de notificación o firma.
   * @param {string} objectType - objectType: 'notifications' o 'signatures'
   * @param {string} [providedToken] - Token opcional para evitar acquireTokenSilent.
   * @returns {Promise<void>} - Promesa con los datos de la respuesta del servicio.
   */
  async createCalendarEvent(alert, asset, objectType, providedToken = null) {
    let accessToken = providedToken

    if (!accessToken) {
      const msalInstance = store.getters.msalInstance
      if (!msalInstance) throw new Error('Instancia MSAL no encontrada')

      const account = msalInstance.getActiveAccount()
      if (!account) throw new Error('No se encontró una cuenta activa')

      console.log('GraphService: Obteniendo token silenciosamente...', {
        username: account.username,
      })

      const response = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read', 'Calendars.ReadWrite'],
        account,
        authority: 'https://login.microsoftonline.com/common/v2.0',
      })
      accessToken = response.accessToken
    }

    return this._createEvent(alert, asset, objectType, accessToken)
  }

  /**
   * Lógica interna para crear un único evento.
   */
  async _createEvent(alert, asset, objectType, accessToken) {
    const assetId = asset?.id || asset['#Id'] || ''
    const concept = asset?.concepto || asset?.purpose || ''

    // Trazar los claims del token para depuración y verificación de permisos
    // Esto es útil para verificar que el token tiene los permisos correctos y para depurar problemas de autorización
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]))
      console.log('GraphService: Token Claims:', {
        scp: payload.scp,
        aud: payload.aud,
        iss: payload.iss,
      })
    } catch (e) {
      console.warn('GraphService: No se pudo decodificar el token')
    }

    const content = i18n.t('calendar.description', {
      assetId,
      concept,
      objectType:
        objectType === 'notifications'
          ? i18n.t('calendar.notification')
          : i18n.t('calendar.signature'),
    })

    const event = {
      subject: alert.text || 'Alerta GoodOk',
      body: {
        contentType: 'HTML',
        content: content,
      },
      start: {
        dateTime: new Date(
          new Date(alert.date).getTime() + 7 * 60 * 60 * 1000
        ).toISOString(),
        timeZone: 'UTC',
      },
      end: {
        dateTime: new Date(
          new Date(alert.date).getTime() + 8 * 60 * 60 * 1000
        ).toISOString(),
        timeZone: 'UTC',
      },
      location: {
        displayName: 'GoodOK',
      },
    }

    const apiResponse = await fetch(
      'https://graph.microsoft.com/v1.0/me/events',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      }
    )

    if (!apiResponse.ok) {
      const errorData = await apiResponse.json().catch(() => ({}))
      console.error('GraphService: Detalle del error', {
        status: apiResponse.status,
        errorData,
      })
      // Es vital incluir "Status: 401" para que AlertDialog.vue y MassiveActionsButtons.vue puedan capturarlo y lanzar el popup
      throw new Error(
        `Error HTTP - Status: ${apiResponse.status}, Message: ${
          errorData.error?.message || apiResponse.statusText || 'Error en Graph'
        }`
      )
    }

    return apiResponse.json().catch(() => null)
  }

  /**
   * Crea múltiples eventos de calendario de forma secuencial.
   * @param {Object} alert - El objeto de la alerta { date, text }.
   * @param {Array} items - Lista de notificaciones o firmas.
   * @param {string} objectType - 'notifications' o 'signatures'
   * @param {string} [providedToken] - Token opcional para evitar acquireTokenSilent.
   */
  async createCalendarEventsBatch(
    alert,
    items,
    objectType,
    providedToken = null
  ) {
    let accessToken = providedToken

    if (!accessToken) {
      const msalInstance = store.getters.msalInstance
      if (!msalInstance) throw new Error('Instancia MSAL no encontrada')

      const account = msalInstance.getActiveAccount()
      if (!account) throw new Error('No se encontró una cuenta activa')

      console.log('GraphService:  Obteniendo token para batch...', {
        itemsCount: items.length,
      })

      const response = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read', 'Calendars.ReadWrite'],
        account,
        authority: 'https://login.microsoftonline.com/common/v2.0',
      })
      accessToken = response.accessToken
    }

    // Procesamos de forma secuencial para evitar límites de concurrencia y asegurar logs claros
    for (const item of items) {
      try {
        await this._createEvent(alert, item, objectType, accessToken)
      } catch (err) {
        console.error('Error creando evento individual en batch:', err)
        // Si es un error de autorización, lo lanzamos para manejar el reintento
        if (err.message.includes('Status: 401')) {
          throw err
        }
        // Para otros errores, continuamos con el siguiente elemento
      }
    }
  }
}

export default new GraphService()
